/**
 * Utility functions to normalize user data between camelCase (frontend) and snake_case (backend)
 */

/**
 * Convert snake_case keys to camelCase
 * @param {Object} obj - Object with snake_case keys
 * @returns {Object} - Object with camelCase keys
 */
export const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item));
  }
  
  const camelObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    
    // Recursively convert nested objects
    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      camelObj[camelKey] = toCamelCase(value);
    } else if (Array.isArray(value)) {
      camelObj[camelKey] = value.map(item => 
        typeof item === 'object' && item !== null ? toCamelCase(item) : item
      );
    } else {
      camelObj[camelKey] = value;
    }
  }
  
  return camelObj;
};

/**
 * Convert camelCase keys to snake_case
 * @param {Object} obj - Object with camelCase keys
 * @returns {Object} - Object with snake_case keys
 */
export const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item));
  }
  
  const snakeObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    
    // Recursively convert nested objects
    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      snakeObj[snakeKey] = toSnakeCase(value);
    } else if (Array.isArray(value)) {
      snakeObj[snakeKey] = value.map(item => 
        typeof item === 'object' && item !== null ? toSnakeCase(item) : item
      );
    } else {
      snakeObj[snakeKey] = value;
    }
  }
  
  return snakeObj;
};

/**
 * Normalize user object to handle both camelCase and snake_case
 * Always returns camelCase for consistent frontend usage
 * @param {Object} user - User object (can be camelCase or snake_case)
 * @returns {Object} - Normalized user object in camelCase
 */
export const normalizeUser = (user) => {
  if (!user) return null;
  
  // Check if already in camelCase (has camelCase keys)
  const hasCamelCase = Object.keys(user).some(key => /[A-Z]/.test(key));
  const hasSnakeCase = Object.keys(user).some(key => key.includes('_'));
  
  // If has both, prefer camelCase (likely already normalized)
  if (hasCamelCase && !hasSnakeCase) {
    return user;
  }
  
  // Convert snake_case to camelCase
  if (hasSnakeCase) {
    return toCamelCase(user);
  }
  
  return user;
};

/**
 * Get field value from user object handling both camelCase and snake_case
 * @param {Object} user - User object
 * @param {string} fieldName - Field name in camelCase (e.g., 'bloodType')
 * @returns {*} - Field value
 */
export const getUserField = (user, fieldName) => {
  if (!user) return null;
  
  // Try camelCase first
  if (user[fieldName] !== undefined) {
    return user[fieldName];
  }
  
  // Try snake_case
  const snakeField = fieldName.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  if (user[snakeField] !== undefined) {
    return user[snakeField];
  }
  
  return null;
};

/**
 * Field mapping for common user fields
 */
export const USER_FIELD_MAP = {
  // Personal info
  name: ['name'],
  email: ['email'],
  phone: ['phone'],
  bloodType: ['blood_type', 'bloodType'],
  gender: ['gender'],
  birthYear: ['birth_year', 'birthYear'],
  
  // Location
  country: ['country'],
  city: ['city'],
  state: ['state'],
  zipCode: ['zip_code', 'zipCode'],
  address: ['address'],
  
  // Donation preferences
  isBloodDonor: ['is_blood_donor', 'isBloodDonor'],
  isOrganDonor: ['is_organ_donor', 'isOrganDonor'],
  organsPledged: ['organs_pledged', 'organsPledged'],
  bodyDonation: ['body_donation', 'bodyDonation'],
  donationType: ['donation_type', 'donationType'],
  
  // Emergency contact
  emergencyContact: ['emergency_contact', 'emergencyContact'],
  
  // Other
  profilePicture: ['profile_picture', 'profilePicture'],
  sosOptIn: ['sos_opt_in', 'sosOptIn'],
};

/**
 * Get field value using field map for maximum compatibility
 * @param {Object} user - User object
 * @param {string} fieldKey - Key from USER_FIELD_MAP
 * @returns {*} - Field value
 */
export const getMappedUserField = (user, fieldKey) => {
  if (!user) return null;
  
  const possibleKeys = USER_FIELD_MAP[fieldKey];
  if (!possibleKeys) {
    return getUserField(user, fieldKey);
  }
  
  for (const key of possibleKeys) {
    if (user[key] !== undefined && user[key] !== null) {
      return user[key];
    }
  }
  
  return null;
};

